import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import Files from "react-butterfiles";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";

import StorefrontIcon from "@material-ui/icons/Storefront";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckIcon from "@material-ui/icons/Check";
import WarningIcon from "@material-ui/icons/Warning";

import { graphqlUrl, endPoint } from "utils/config";
import FunMap from "commonComponents/addShop/FunMap";
import Button from "designV2/components/Button";
import Field from "designV2/components/Field";
import Panel from "designV2/components/Panel";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import StatusBadge from "designV2/components/StatusBadge";
import "./editShop.css";

/* ------------------------------------------------------------------ *
 * GraphQL — same operations commonComponents/addShop/AddShop.js uses
 * for the shopAdmin edit case, kept separate here rather than shared
 * because that component also drives shop creation and the super
 * admin's flows; this screen only ever needs the "edit my own shop"
 * slice of it.
 * ------------------------------------------------------------------ */

const UPDATE_SHOP = gql`
  mutation updateShopByShopAdmin(
    $budget: Budget
    $active: Boolean
    $rootCategory: ID
    $name: String
    $preparingTime: Int!
    $minimumPurchase: Int!
    $newOrderSMSNotificationPhoneNumber: String
    $newOrderSMSNotificationEnabled: Boolean!
    $address: String
    $categories: [ID]
    $phoneNumbers: [String!]!
    $location: ShopLocationInput!
    $origin: String
    $description: String
    $workingHoursInMinutes: [workingHoursInMinutesInput]
    $notWorkingDays: [notWorkingDaysInput]
    $bannerUrl: String
    $logoUrl: String
  ) {
    updateShopByShopAdmin(
      data: {
        budget: $budget
        active: $active
        name: $name
        rootCategory: $rootCategory
        address: $address
        preparingTime: $preparingTime
        minimumPurchase: $minimumPurchase
        newOrderSMSNotificationPhoneNumber: $newOrderSMSNotificationPhoneNumber
        newOrderSMSNotificationEnabled: $newOrderSMSNotificationEnabled
        phoneNumbers: $phoneNumbers
        location: $location
        origin: $origin
        categories: $categories
        description: $description
        workingHoursInMinutes: $workingHoursInMinutes
        notWorkingDays: $notWorkingDays
        bannerUrl: $bannerUrl
        logoUrl: $logoUrl
      }
    ) {
      _id
    }
  }
`;

const UPDATE_SHOP_AFTER_REJECTION = gql`
  mutation updateShopAfterRejectedByShopAdmin(
    $budget: Budget
    $active: Boolean
    $rootCategory: ID
    $name: String
    $preparingTime: Int!
    $minimumPurchase: Int!
    $newOrderSMSNotificationPhoneNumber: String
    $newOrderSMSNotificationEnabled: Boolean!
    $address: String
    $categories: [ID]
    $phoneNumbers: [String!]!
    $location: ShopLocationInput!
    $origin: String
    $description: String
    $workingHoursInMinutes: [workingHoursInMinutesInput]
    $notWorkingDays: [notWorkingDaysInput]
    $bannerUrl: String
    $logoUrl: String
  ) {
    updateShopAfterRejectedByShopAdmin(
      data: {
        budget: $budget
        active: $active
        name: $name
        rootCategory: $rootCategory
        address: $address
        preparingTime: $preparingTime
        minimumPurchase: $minimumPurchase
        newOrderSMSNotificationPhoneNumber: $newOrderSMSNotificationPhoneNumber
        newOrderSMSNotificationEnabled: $newOrderSMSNotificationEnabled
        phoneNumbers: $phoneNumbers
        location: $location
        origin: $origin
        categories: $categories
        description: $description
        workingHoursInMinutes: $workingHoursInMinutes
        notWorkingDays: $notWorkingDays
        bannerUrl: $bannerUrl
        logoUrl: $logoUrl
      }
    ) {
      _id
      rootCategory {
        _id
        title
      }
    }
  }
`;

const UPLOAD_FILE = gql`
  mutation uploadFile($file: Upload!, $folderName: String!) {
    uploadFile(data: { file: $file, folderName: $folderName }) {
      url
    }
  }
`;

const GET_CATEGORIES = gql`
  query getCategories($parent: ID) {
    getCategories(filters: { parent: $parent }) {
      _id
      title
      parent {
        title
      }
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const BUDGET_OPTIONS = [
  { value: "B", label: "$", hint: "Budget-friendly" },
  { value: "BB", label: "$$", hint: "Moderate" },
  { value: "BBB", label: "$$$", hint: "Premium" },
];

// The "WEN" for Wednesday isn't a typo here — it's what the backend's
// workingHoursInMinutesInput.type actually expects, matching every other
// screen that writes working hours. Changing it would silently stop
// Wednesday hours from saving.
const WEEK_DAYS = [
  { value: "MON", label: "Monday" },
  { value: "TUE", label: "Tuesday" },
  { value: "WEN", label: "Wednesday" },
  { value: "THU", label: "Thursday" },
  { value: "FRI", label: "Friday" },
  { value: "SAT", label: "Saturday" },
  { value: "SUN", label: "Sunday" },
];

const DEFAULT_POSITION = { lat: 43.6532, lng: -79.3832 };

const minutesToTime = (mins) => moment().startOf("day").add(mins, "minutes").format("HH:mm");
const timeToMinutes = (time) => moment(time, "HH:mm").diff(moment(time, "HH:mm").startOf("day"), "minutes");

const readableError = (error) => {
  if (!error) return "";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  return error.message || "Something went wrong, please try again.";
};

const copyToClipboard = (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      resolve();
    } catch (err) {
      reject(err);
    }
  });
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const EditShopForm = ({ shop, refetch, editAfterRejection }) => {
  const rejectionMessage =
    editAfterRejection && typeof window !== "undefined" ? window.localStorage.getItem("rejectionMessage") : null;

  const [name, setName] = useState(shop.name || "");
  const [description, setDescription] = useState(shop.description || "");
  const [phoneNumbers, setPhoneNumbers] = useState([
    shop.phoneNumbers[0] || "",
    shop.phoneNumbers[1] || "",
    shop.phoneNumbers[2] || "",
  ]);
  const [preparingTime, setPreparingTime] = useState(shop.preparingTime != null ? String(shop.preparingTime) : "");
  const [minimumPurchase, setMinimumPurchase] = useState(
    shop.minimumPurchase != null ? String(shop.minimumPurchase) : "0",
  );
  const [smsEnabled, setSmsEnabled] = useState(Boolean(shop.newOrderSMSNotificationEnabled));
  const [smsPhone, setSmsPhone] = useState(shop.newOrderSMSNotificationPhoneNumber || "");
  const [town, setTown] = useState(shop.origin || "");
  const [address, setAddress] = useState(shop.address || "");
  const [budget, setBudget] = useState(shop.budget || "B");
  const [active, setActive] = useState(Boolean(shop.active));
  const [bannerImage, setBannerImage] = useState(shop.bannerUrl || "");
  const [logoImage, setLogoImage] = useState(shop.logoUrl || "");
  const [markerPosition, setMarkerPosition] = useState(
    shop.location && shop.location.coordinates
      ? { lat: shop.location.coordinates[1], lng: shop.location.coordinates[0] }
      : DEFAULT_POSITION,
  );
  const [rootCategoryId, setRootCategoryId] = useState(shop.rootCategory._id);
  const [rootCategoryTitle, setRootCategoryTitle] = useState(shop.rootCategory.title);
  const [categoryIds, setCategoryIds] = useState((shop.categories || []).map((c) => c._id));

  const [workingHours, setWorkingHours] = useState(() => {
    const seed = {};
    (shop.workingHoursInMinutes || []).forEach((item) => {
      seed[item.type] = { from: minutesToTime(item.from), to: minutesToTime(item.to) };
    });
    return seed;
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saved, setSaved] = useState(false);
  const [copiedReferral, setCopiedReferral] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const { data: rootCategoriesData } = useQuery(GET_CATEGORIES, { variables: { parent: null } });
  const { data: categoriesData } = useQuery(GET_CATEGORIES, {
    variables: { parent: rootCategoryId },
    skip: !rootCategoryId,
  });

  const [uploadFile] = useMutation(UPLOAD_FILE);
  const [updateShop, { loading: saving }] = useMutation(UPDATE_SHOP);
  const [updateShopAfterRejection, { loading: resubmitting }] = useMutation(UPDATE_SHOP_AFTER_REJECTION);

  const categoryLabel = rootCategoryTitle === "Restaurant" ? "Cuisine" : "Shop type";

  const toggleDay = (dayValue) => {
    setWorkingHours((current) => {
      if (current[dayValue]) {
        const next = { ...current };
        delete next[dayValue];
        return next;
      }
      return { ...current, [dayValue]: { from: "09:00", to: "17:00" } };
    });
  };

  const setDayTime = (dayValue, field, value) => {
    setWorkingHours((current) => ({
      ...current,
      [dayValue]: { ...current[dayValue], [field]: value },
    }));
  };

  const firstOpenDay = WEEK_DAYS.find((d) => workingHours[d.value]);
  const applyFirstDayToAll = () => {
    if (!firstOpenDay) return;
    const hours = workingHours[firstOpenDay.value];
    const next = {};
    WEEK_DAYS.forEach((d) => {
      next[d.value] = { ...hours };
    });
    setWorkingHours(next);
  };

  const toggleCategory = (id) => {
    setCategoryIds((current) => (current.includes(id) ? current.filter((c) => c !== id) : [...current, id]));
  };

  const setPhoneNumber = (index, value) => {
    setPhoneNumbers((current) => {
      const next = [...current];
      next[index] = value;
      return next;
    });
  };

  const uploadImage = (file, kind) => {
    const setUploading = kind === "banner" ? setUploadingBanner : setUploadingLogo;
    const setImage = kind === "banner" ? setBannerImage : setLogoImage;
    setUploading(true);
    uploadFile({ variables: { file: file.src.file, folderName: kind === "banner" ? "bannerImage" : "logoImage" } })
      .then((res) => {
        setImage(res.data.uploadFile.url);
        setUploading(false);
      })
      .catch((err) => {
        setUploading(false);
        setFormError(readableError(err));
      });
  };

  const handleCopyReferral = () => {
    if (!shop.shopAdminReferralCode) return;
    copyToClipboard(shop.shopAdminReferralCode)
      .then(() => {
        setCopiedReferral(true);
        window.setTimeout(() => setCopiedReferral(false), 1500);
      })
      .catch(() => {});
  };

  const validate = () => {
    const errors = {};
    if (!name.trim()) errors.name = "Give your shop a name.";
    if (!description.trim()) errors.description = "Add a short description.";
    if (!phoneNumbers[0].trim()) errors.phone1 = "At least one phone number is required.";
    if (!preparingTime) {
      errors.preparingTime = "Preparing time can't be empty.";
    } else if (!/^([1-9]|[1-5]\d|60)$/.test(String(preparingTime)) || Number(preparingTime) < 5) {
      errors.preparingTime = "Enter a number between 5 and 60.";
    }
    if (!String(minimumPurchase).trim()) {
      errors.minimumPurchase = "Set an amount, or 0 if you don't offer free delivery.";
    }
    if (smsEnabled && !smsPhone.trim()) {
      errors.smsPhone = "Enter a phone number to enable order SMS notifications.";
    }
    if (!town.trim()) errors.town = "Town / city is required.";
    if (!address.trim()) errors.address = "Address is required.";
    return errors;
  };

  const handleSubmit = () => {
    if (saving || resubmitting) return;
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError("Fix the highlighted fields and try again.");
      return;
    }
    setFieldErrors({});
    setFormError("");
    setSaved(false);

    const variables = {
      name: name.trim(),
      description: description.trim(),
      origin: town.trim(),
      address: address.trim(),
      preparingTime: parseInt(preparingTime, 10),
      minimumPurchase: parseInt(minimumPurchase, 10),
      newOrderSMSNotificationPhoneNumber: smsPhone,
      newOrderSMSNotificationEnabled: smsEnabled,
      phoneNumbers: phoneNumbers.filter(Boolean),
      location: { coordinates: [parseFloat(markerPosition.lng), parseFloat(markerPosition.lat)], type: "Point" },
      budget,
      active,
      bannerUrl: bannerImage,
      logoUrl: logoImage,
      rootCategory: rootCategoryId,
      categories: categoryIds,
      notWorkingDays: WEEK_DAYS.filter((d) => !workingHours[d.value]).map((d) => ({ type: d.value })),
      workingHoursInMinutes: Object.keys(workingHours).map((day) => ({
        type: day,
        from: timeToMinutes(workingHours[day].from),
        to: timeToMinutes(workingHours[day].to),
      })),
    };

    if (editAfterRejection) {
      updateShopAfterRejection({ variables })
        .then((res) => {
          window.localStorage.setItem(
            "shopType",
            res.data.updateShopAfterRejectedByShopAdmin.rootCategory.title.toString(),
          );
          window.localStorage.removeItem("rejectionMessage");
          window.location.href = endPoint + window.localStorage.getItem("nextStep");
        })
        .catch((err) => setFormError(readableError(err)));
    } else {
      updateShop({ variables })
        .then(() => {
          setSaved(true);
          window.setTimeout(() => setSaved(false), 4000);
          if (refetch) refetch();
        })
        .catch((err) => setFormError(readableError(err)));
    }
  };

  const rootCategories = (rootCategoriesData && rootCategoriesData.getCategories) || [];
  const subCategories = (categoriesData && categoriesData.getCategories) || [];

  const formBody = (
    <div className="editShopForm">
      {rejectionMessage && (
        <div className="editShopRejectionBanner">
          <WarningIcon fontSize="small" />
          <div>
            <strong>Your shop needs a few changes before it can go live.</strong>
            <p>{rejectionMessage}</p>
          </div>
        </div>
      )}

      {formError && (
        <div className="dsFormMessage error" role="alert">
          {formError}
        </div>
      )}
      {saved && (
        <div className="dsFormMessage success" role="status">
          <CheckIcon fontSize="small" /> Saved. Your changes are live.
        </div>
      )}

      {/* PROFILE */}
      <Panel title="Shop profile" subtitle="How your shop appears to customers.">
        <div className="editShopPreview">
          <div className="editShopPreviewBanner">
            {bannerImage ? (
              <img src={`${graphqlUrl}${bannerImage}`} alt="Shop banner" />
            ) : (
              <StorefrontIcon fontSize="large" />
            )}
          </div>
          <div className="editShopPreviewRow">
            <div className="editShopPreviewLogo">
              {logoImage ? <img src={`${graphqlUrl}${logoImage}`} alt="Shop logo" /> : <StorefrontIcon />}
            </div>
            <div className="editShopPreviewText">
              <h3>{name || "Your shop name"}</h3>
              <p>{description || "A short description of your shop"}</p>
            </div>
          </div>
        </div>

        <div className="editShopGrid">
          <Field label="Shop name" required error={fieldErrors.name}>
            <input className="dsInput" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Price range">
            <div className="editShopBudgetRow">
              {BUDGET_OPTIONS.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  className={`editShopBudgetOption ${budget === opt.value ? "isSelected" : ""}`}
                  onClick={() => setBudget(opt.value)}
                  title={opt.hint}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Field>
        </div>

        <Field label="Description" required error={fieldErrors.description} hint="Shown on your shop's page.">
          <textarea
            className="dsInput editShopTextarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <Field label="Shop type" hint="Changing this affects which categories and products are available.">
          <div className="editShopOptionRow">
            {rootCategories.map((cat) => (
              <label key={cat._id} className={`dsOptionCard ${rootCategoryId === cat._id ? "isSelected" : ""}`}>
                <input
                  type="radio"
                  checked={rootCategoryId === cat._id}
                  onChange={() => {
                    setRootCategoryId(cat._id);
                    setRootCategoryTitle(cat.title);
                    setCategoryIds([]);
                  }}
                />
                <span className="dsOptionCardText">
                  <strong>{cat.title === "Restaurant" ? "Restaurant" : "Shop"}</strong>
                </span>
              </label>
            ))}
          </div>
        </Field>

        {subCategories.length > 0 && (
          <Field label={categoryLabel}>
            <div className="editShopOptionRow">
              {subCategories.map((cat) => (
                <label key={cat._id} className={`dsOptionCard ${categoryIds.includes(cat._id) ? "isSelected" : ""}`}>
                  <input
                    type="checkbox"
                    checked={categoryIds.includes(cat._id)}
                    onChange={() => toggleCategory(cat._id)}
                  />
                  <span className="dsOptionCardText">{cat.title}</span>
                </label>
              ))}
            </div>
          </Field>
        )}

        <div className="editShopGrid">
          <Field label="Banner image" hint="Shown at the top of your shop's page. Under 2MB.">
            <Files
              maxSize="2mb"
              multiple={false}
              accept={["image/jpg", "image/jpeg", "image/png"]}
              onSuccess={(files) => uploadImage(files[0], "banner")}
            >
              {({ browseFiles, getDropZoneProps }) => (
                <div {...getDropZoneProps({ className: "editShopImageUpload" })}>
                  {uploadingBanner ? (
                    <CircularProgress size={22} />
                  ) : bannerImage ? (
                    <img src={`${graphqlUrl}${bannerImage}`} alt="Banner preview" />
                  ) : (
                    <StorefrontIcon />
                  )}
                  <Button size="sm" onClick={browseFiles} type="button">
                    {bannerImage ? "Change image" : "Select image"}
                  </Button>
                </div>
              )}
            </Files>
          </Field>
          <Field label="Logo image" hint="Shown next to your shop's name. Under 2MB.">
            <Files
              maxSize="2mb"
              multiple={false}
              accept={["image/jpg", "image/jpeg", "image/png"]}
              onSuccess={(files) => uploadImage(files[0], "logo")}
            >
              {({ browseFiles, getDropZoneProps }) => (
                <div {...getDropZoneProps({ className: "editShopImageUpload" })}>
                  {uploadingLogo ? (
                    <CircularProgress size={22} />
                  ) : logoImage ? (
                    <img src={`${graphqlUrl}${logoImage}`} alt="Logo preview" />
                  ) : (
                    <StorefrontIcon />
                  )}
                  <Button size="sm" onClick={browseFiles} type="button">
                    {logoImage ? "Change image" : "Select image"}
                  </Button>
                </div>
              )}
            </Files>
          </Field>
        </div>
      </Panel>

      {/* STATUS */}
      <Panel title="Status" subtitle="What customers see and what only you can change.">
        <div className="editShopStatusRow">
          <div className="editShopStatusItem">
            <span className="editShopStatusLabel">Accepting orders</span>
            <Switch checked={active} onChange={(e) => setActive(e.target.checked)} color="primary" />
          </div>
          <div className="editShopStatusItem">
            <span className="editShopStatusLabel">Account status</span>
            <StatusBadge status={shop.state === "SUSPENDED" ? "danger" : "success"} label={shop.state || "ACTIVE"} />
          </div>
          <div className="editShopStatusItem">
            <span className="editShopStatusLabel">Cash accepted</span>
            <StatusBadge status={shop.acceptCash ? "success" : "neutral"} label={shop.acceptCash ? "Yes" : "No"} />
          </div>
        </div>
        {shop.state === "SUSPENDED" && (
          <div className="dsFormMessage warning">
            <WarningIcon fontSize="small" />
            <span>Your shop is suspended by the platform and won't take new orders until this changes.</span>
          </div>
        )}
        {shop.shopAdminReferralCode && (
          <Field label="Your referral code" hint="Share this code — new shop admins can use it when they sign up.">
            <div className="editShopReferralRow">
              <code>{shop.shopAdminReferralCode}</code>
              <Button
                size="sm"
                variant={copiedReferral ? "primary" : "secondary"}
                icon={<FileCopyIcon style={{ fontSize: 14 }} />}
                onClick={handleCopyReferral}
              >
                {copiedReferral ? "Copied" : "Copy"}
              </Button>
            </div>
          </Field>
        )}
      </Panel>

      {/* CONTACT & LOCATION */}
      <Panel title="Contact & location" subtitle="How customers and drivers reach you.">
        <div className="editShopGrid">
          <Field label="Phone number" required error={fieldErrors.phone1}>
            <input className="dsInput" value={phoneNumbers[0]} onChange={(e) => setPhoneNumber(0, e.target.value)} />
          </Field>
          <Field label="Phone number" optional>
            <input className="dsInput" value={phoneNumbers[1]} onChange={(e) => setPhoneNumber(1, e.target.value)} />
          </Field>
          <Field label="Phone number" optional>
            <input className="dsInput" value={phoneNumbers[2]} onChange={(e) => setPhoneNumber(2, e.target.value)} />
          </Field>
          <Field label="Town / city" required error={fieldErrors.town}>
            <input className="dsInput" value={town} onChange={(e) => setTown(e.target.value)} />
          </Field>
        </div>
        <Field label="Address" required error={fieldErrors.address} hint="Search below or click the map to update it.">
          <input className="dsInput" value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        <div className="editShopMap">
          <FunMap
            position={markerPosition}
            city={town}
            setCity={setTown}
            setStreet={() => {}}
            setProvince={() => {}}
            setPostalCode={() => {}}
            setAddress={setAddress}
            onSetmarkerPosition={(lat, lng) => setMarkerPosition({ lat, lng })}
          />
        </div>
      </Panel>

      {/* HOURS */}
      <Panel
        title="Hours"
        subtitle="Days you don't open are treated as closed all day."
        actions={
          firstOpenDay && (
            <Button size="sm" onClick={applyFirstDayToAll} type="button">
              Copy {firstOpenDay.label}'s hours to every day
            </Button>
          )
        }
      >
        <div className="editShopDayList">
          {WEEK_DAYS.map((day) => {
            const isOpen = Boolean(workingHours[day.value]);
            return (
              <div key={day.value} className={`editShopDayRow ${isOpen ? "isOpen" : ""}`}>
                <label className="editShopDayToggle">
                  <input type="checkbox" checked={isOpen} onChange={() => toggleDay(day.value)} />
                  {day.label}
                </label>
                {isOpen ? (
                  <div className="editShopDayTimes">
                    <input
                      type="time"
                      className="dsInput"
                      value={workingHours[day.value].from}
                      onChange={(e) => setDayTime(day.value, "from", e.target.value)}
                    />
                    <span>to</span>
                    <input
                      type="time"
                      className="dsInput"
                      value={workingHours[day.value].to}
                      onChange={(e) => setDayTime(day.value, "to", e.target.value)}
                    />
                  </div>
                ) : (
                  <span className="editShopDayClosed">Closed</span>
                )}
              </div>
            );
          })}
        </div>
      </Panel>

      {/* DELIVERY & NOTIFICATIONS */}
      <Panel title="Delivery & notifications">
        <div className="editShopGrid">
          <Field
            label="Order preparing time"
            required
            error={fieldErrors.preparingTime}
            hint="Minutes, between 5 and 60."
          >
            <input
              type="number"
              className="dsInput"
              value={preparingTime}
              onChange={(e) => setPreparingTime(e.target.value)}
            />
          </Field>
          <Field
            label="Free delivery minimum"
            required
            error={fieldErrors.minimumPurchase}
            hint="Order total that unlocks free delivery. Use 0 to turn this off."
          >
            <input
              type="number"
              className="dsInput"
              value={minimumPurchase}
              onChange={(e) => setMinimumPurchase(e.target.value)}
            />
          </Field>
        </div>

        <Field label="SMS me about new orders">
          <label className="editShopSwitchRow">
            <Switch checked={smsEnabled} onChange={(e) => setSmsEnabled(e.target.checked)} color="primary" />
            <span>{smsEnabled ? "Enabled" : "Disabled"}</span>
          </label>
        </Field>
        {smsEnabled && (
          <Field label="Notification phone number" required error={fieldErrors.smsPhone}>
            <input className="dsInput" value={smsPhone} onChange={(e) => setSmsPhone(e.target.value)} />
          </Field>
        )}
      </Panel>
    </div>
  );

  const actionBar = (
    <div className="editShopActionBar">
      <Button
        variant="primary"
        onClick={handleSubmit}
        loading={saving || resubmitting}
      >
        {editAfterRejection ? "Resubmit for review" : "Save changes"}
      </Button>
    </div>
  );

  if (editAfterRejection) {
    return (
      <div className="editShopEmbedded">
        {formBody}
        {actionBar}
      </div>
    );
  }

  return (
    <div className="page editShopPage">
      <main className="main">
        <PageHeader
          title="Shop settings"
          description="Everything customers see, plus how your shop runs day to day."
          actions={
            <PageActionButton
              variant="primary"
              icon={<CheckIcon fontSize="small" />}
              label="Save changes"
              info={saving ? "Saving..." : "Update your shop"}
              onClick={handleSubmit}
              loading={saving}
            />
          }
        />
        {formBody}
      </main>
    </div>
  );
};

export default EditShopForm;
